import { useState } from "react";
import { Link } from "react-router-dom";
import { ebayAPI } from "@/lib/api";
import {
  ArrowLeft, TShirt, Spinner, CheckCircle, Warning, ArrowRight,
  ArrowSquareOut, MagicWand,
} from "@phosphor-icons/react";

const rowKey = (sku, aspect) => `${sku}|${aspect}`;

export default function SizeCheckPage() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [fixing, setFixing] = useState({});      // key -> true
  const [fixedKeys, setFixedKeys] = useState({}); // key -> true
  const [failedKeys, setFailedKeys] = useState({}); // key -> error msg
  const [picks, setPicks] = useState({});        // key -> chosen value
  const [bulkFixing, setBulkFixing] = useState(false);

  const scan = async () => {
    setScanning(true);
    setError("");
    setResult(null);
    setFixedKeys({});
    setFailedKeys({});
    setPicks({});
    try {
      const { data } = await ebayAPI.sizeScan();
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Scan failed. Try again.");
    } finally {
      setScanning(false);
    }
  };

  const applyFixes = async (fixes) => {
    const { data } = await ebayAPI.sizeFix(fixes);
    const okSkus = new Set((data.results || []).filter((r) => r.success).map((r) => r.sku));
    const errBySku = {};
    for (const r of data.results || []) {
      if (!r.success) errBySku[r.sku] = r.error || "Failed";
    }
    setFixedKeys((prev) => {
      const next = { ...prev };
      for (const f of fixes) if (okSkus.has(f.sku)) next[rowKey(f.sku, f.aspect)] = true;
      return next;
    });
    setFailedKeys((prev) => {
      const next = { ...prev };
      for (const f of fixes) {
        const k = rowKey(f.sku, f.aspect);
        if (errBySku[f.sku]) next[k] = errBySku[f.sku];
        else delete next[k];
      }
      return next;
    });
    return data;
  };

  const fixOne = async (sku, aspect, value) => {
    const key = rowKey(sku, aspect);
    setFixing((p) => ({ ...p, [key]: true }));
    try {
      await applyFixes([{ sku, aspect, value }]);
    } catch (err) {
      setFailedKeys((p) => ({ ...p, [key]: err?.response?.data?.detail || "Failed" }));
    } finally {
      setFixing((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
    }
  };

  const pendingAutoFixes = (result?.flagged || []).flatMap((f) =>
    f.issues
      .filter((i) => i.suggested && !fixedKeys[rowKey(f.sku, i.aspect)])
      .map((i) => ({ sku: f.sku, aspect: i.aspect, value: i.suggested }))
  );

  const fixAll = async () => {
    if (pendingAutoFixes.length === 0) return;
    setBulkFixing(true);
    try {
      await applyFixes(pendingAutoFixes);
    } catch (err) {
      setError(err?.response?.data?.detail || "Bulk fix failed.");
    } finally {
      setBulkFixing(false);
    }
  };

  const flagged = result?.flagged || [];
  const openCount = flagged.reduce(
    (n, f) => n + f.issues.filter((i) => !fixedKeys[rowKey(f.sku, i.aspect)]).length,
    0
  );

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b-4 border-black">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/profile" className="border-2 border-black p-2 active:translate-y-[1px]" data-testid="size-check-back-btn">
            <ArrowLeft size={18} weight="bold" />
          </Link>
          <TShirt size={22} weight="bold" />
          <h1 className="font-heading text-2xl font-black uppercase tracking-tighter">Size Check</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">
        {/* Intro + scan */}
        <div className="border-4 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <p className="font-body text-sm text-neutral-700">
            eBay now requires <strong>standardized sizes</strong> on apparel & footwear listings.
            Listings with custom sizes get <strong>hidden</strong> until fixed. Scan your live
            listings to find and fix them in one tap.
          </p>
          <button
            onClick={scan}
            disabled={scanning}
            className="mt-3 w-full border-2 border-black bg-black text-white py-3 font-heading text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 active:translate-y-[1px] disabled:opacity-50"
            data-testid="size-check-scan-btn"
          >
            {scanning ? <Spinner size={16} weight="bold" className="animate-spin" /> : <TShirt size={16} weight="bold" />}
            {scanning ? "Scanning your listings…" : result ? "Scan Again" : "Scan My Listings"}
          </button>
          {scanning && (
            <p className="mt-2 font-body text-xs text-neutral-500 text-center" data-testid="size-check-scanning-note">
              Checking every live listing against eBay's standardized size lists — this can take up to a minute.
            </p>
          )}
        </div>

        {error && (
          <div className="border-4 border-black bg-red-50 p-3 flex items-start gap-2" data-testid="size-check-error">
            <Warning size={18} weight="bold" className="text-red-600 mt-0.5 shrink-0" />
            <span className="font-body text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Results */}
        {result && (
          <>
            <div className="border-4 border-black p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="size-check-summary">
              <span className="font-heading text-sm font-bold uppercase">
                {result.scanned} scanned
              </span>
              <span className="font-heading text-sm font-bold uppercase text-green-700">
                {result.compliant} OK
              </span>
              <span className={`font-heading text-sm font-bold uppercase ${flagged.length > 0 ? "text-red-600" : "text-neutral-400"}`}>
                {flagged.length} flagged
              </span>
            </div>

            {flagged.length === 0 ? (
              result.scanned === 0 ? (
                <div className="border-4 border-black bg-neutral-50 p-6 text-center" data-testid="size-check-no-listings">
                  <TShirt size={40} weight="bold" className="mx-auto text-neutral-400 mb-2" />
                  <div className="font-heading text-lg font-black uppercase tracking-tight">No Live Listings Found</div>
                  <p className="font-body text-sm text-neutral-600 mt-1">
                    No published eBay listings to check on this account.
                  </p>
                </div>
              ) : (
                <div className="border-4 border-black bg-green-50 p-6 text-center" data-testid="size-check-all-clear">
                  <CheckCircle size={40} weight="bold" className="mx-auto text-green-600 mb-2" />
                  <div className="font-heading text-lg font-black uppercase tracking-tight">All Sizes Standard</div>
                  <p className="font-body text-sm text-neutral-600 mt-1">
                    Every live listing uses eBay-supported size values. Nothing to fix.
                  </p>
                </div>
              )
            ) : (
              <>
                {pendingAutoFixes.length > 0 && (
                  <button
                    onClick={fixAll}
                    disabled={bulkFixing}
                    className="w-full border-2 border-black bg-green-600 text-white py-3 font-heading text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 active:translate-y-[1px] disabled:opacity-50"
                    data-testid="size-check-fix-all-btn"
                  >
                    {bulkFixing ? <Spinner size={16} weight="bold" className="animate-spin" /> : <MagicWand size={16} weight="bold" />}
                    {bulkFixing ? "Fixing…" : `Fix All Auto-Matched (${pendingAutoFixes.length})`}
                  </button>
                )}

                {openCount === 0 && (
                  <div className="border-4 border-black bg-green-50 p-4 text-center" data-testid="size-check-all-fixed">
                    <CheckCircle size={28} weight="bold" className="mx-auto text-green-600 mb-1" />
                    <div className="font-heading text-sm font-bold uppercase">All flagged sizes fixed on eBay</div>
                  </div>
                )}

                {flagged.map((f) => (
                  <div key={f.sku} className="border-4 border-black" data-testid={`size-check-card-${f.sku}`}>
                    <div className="p-3 flex items-center gap-3 border-b-2 border-black bg-neutral-50">
                      <div className="w-12 h-12 border-2 border-black bg-neutral-100 overflow-hidden shrink-0">
                        {f.image ? <img src={f.image} alt="" className="w-full h-full object-cover" /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-body text-sm font-semibold truncate" title={f.title}>{f.title}</div>
                        <div className="font-mono text-[11px] text-neutral-500 truncate">{f.sku}</div>
                      </div>
                      {f.listing_id && (
                        <a
                          href={`https://www.ebay.com/itm/${f.listing_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-neutral-500 hover:text-black shrink-0"
                          title="View on eBay"
                          data-testid={`size-check-ebay-link-${f.sku}`}
                        >
                          <ArrowSquareOut size={16} weight="bold" />
                        </a>
                      )}
                    </div>

                    <div className="p-3 space-y-3">
                      {f.issues.map((i) => {
                        const key = rowKey(f.sku, i.aspect);
                        const isFixed = !!fixedKeys[key];
                        const isFixing = !!fixing[key] || bulkFixing;
                        const failMsg = failedKeys[key];
                        const pickVal = picks[key] || "";
                        return (
                          <div key={key} data-testid={`size-check-issue-${f.sku}-${i.aspect.replace(/\s/g, "-")}`}>
                            <div className="font-heading text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                              {i.aspect}
                            </div>
                            {isFixed ? (
                              <div className="flex items-center gap-2 text-green-700" data-testid={`size-check-fixed-${f.sku}-${i.aspect.replace(/\s/g, "-")}`}>
                                <CheckCircle size={16} weight="bold" />
                                <span className="font-body text-sm font-semibold">
                                  Updated to "{i.suggested || pickVal}" on eBay
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="border-2 border-red-500 bg-red-50 text-red-700 px-2 py-1 font-mono text-xs line-through">
                                  {i.current}
                                </span>
                                <ArrowRight size={14} weight="bold" className="text-neutral-400 shrink-0" />
                                {i.suggested ? (
                                  <span className="border-2 border-green-600 bg-green-50 text-green-700 px-2 py-1 font-mono text-xs font-bold">
                                    {i.suggested}
                                  </span>
                                ) : (
                                  <select
                                    value={pickVal}
                                    onChange={(e) => setPicks((p) => ({ ...p, [key]: e.target.value }))}
                                    className="border-2 border-black px-2 py-1 font-body text-xs max-w-[160px]"
                                    data-testid={`size-check-pick-${f.sku}-${i.aspect.replace(/\s/g, "-")}`}
                                  >
                                    <option value="">Pick a size…</option>
                                    {i.allowed.map((v) => (
                                      <option key={v} value={v}>{v}</option>
                                    ))}
                                  </select>
                                )}
                                <button
                                  onClick={() => fixOne(f.sku, i.aspect, i.suggested || pickVal)}
                                  disabled={isFixing || (!i.suggested && !pickVal)}
                                  className="border-2 border-black bg-black text-white px-3 py-1 font-heading text-[11px] font-bold uppercase active:translate-y-[1px] disabled:opacity-40"
                                  data-testid={`size-check-fix-btn-${f.sku}-${i.aspect.replace(/\s/g, "-")}`}
                                >
                                  {isFixing ? <Spinner size={12} weight="bold" className="animate-spin" /> : "Fix"}
                                </button>
                              </div>
                            )}
                            {failMsg && !isFixed && (
                              <p className="mt-1 font-body text-[11px] text-red-600" data-testid={`size-check-fail-${f.sku}-${i.aspect.replace(/\s/g, "-")}`}>
                                {failMsg}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
